import { useState } from 'react';
import { ChevronDown, Info, ShieldAlert } from 'lucide-react';
import { cn } from '../../utils/cn';
import { formatLocalDateTime } from '../../utils/timestamps';
import { formatCount, relativeTime } from '../../utils/logbook';

interface LogbookCompletenessProps {
  /** The completeness sentence from the result. It always warns that producer backlog may exist. */
  completeness?: string | null;
  /** The servers the answer actually covered. */
  scope?: string[];
  /** Ingestion high water the page is pinned to. */
  snapshot?: number | null;
  /** Number of `logbook.gap` records in the current result set. */
  gapCount?: number;
  /** Oldest sync receipt across the servers in scope, which bounds how fresh this can be. */
  oldestReceiptAt?: number | null;
  className?: string;
  /** Tour anchor, passed through to the root so a guide can point at the coverage line. */
  'data-tour'?: string;
}

/**
 * What this result does not cover.
 *
 * Logbook is explicit that a central answer is never a claim of complete history: a producer can be
 * holding a local backlog, a snapshot excludes anything inserted after the first page, and a capture
 * gap means evidence was lost rather than absent. `docs/INTEGRATIONS.md` requires those limits to
 * stay visible in product UI, so this sits with the results rather than behind a help link.
 *
 * It stays a single quiet line until there is a gap, which is the one case worth interrupting for.
 */
export const LogbookCompleteness = ({
  completeness, scope, snapshot, gapCount = 0, oldestReceiptAt, className, 'data-tour': tourAnchor,
}: LogbookCompletenessProps) => {
  const [open, setOpen] = useState(false);
  if (!completeness && !scope?.length && !gapCount) return null;

  const serious = gapCount > 0;

  return (
    <div
      className={cn('rounded-lg border px-3 py-2', serious
        ? 'border-yellow-500/25 bg-yellow-500/[0.07]'
        : 'border-glass-200/10 bg-glass-200/[0.03]', className)}
      data-tour={tourAnchor}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="w-full flex items-start gap-2 text-left"
      >
        {serious
          ? <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--tone-warning-text)' }} />
          : <Info className="w-4 h-4 shrink-0 mt-0.5 text-glass-400" />}
        <span className="flex-1 min-w-0 text-xs">
          <span className={cn('font-medium', serious ? 'text-yellow-200' : 'text-glass-300')}>
            {serious
              ? `${formatCount(gapCount)} capture ${gapCount === 1 ? 'gap' : 'gaps'} in these results`
              : 'Coverage'}
          </span>
          <span className="text-glass-500">
            {serious
              ? '. Evidence for those intervals is incomplete.'
              : oldestReceiptAt
                ? `. Oldest server receipt ${relativeTime(oldestReceiptAt)}.`
                : '. May not include every record.'}
          </span>
        </span>
        <ChevronDown className={cn('w-3.5 h-3.5 shrink-0 mt-0.5 text-glass-400 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      {open && (
        <div className="mt-2 pl-6 space-y-2 text-xs text-glass-400">
          {completeness && <p>{completeness}</p>}
          {snapshot != null && snapshot > 0 && (
            <p>
              <span className="text-glass-500">Snapshot </span>
              <span className="font-mono tabular-nums text-glass-300">{snapshot.toLocaleString()}</span>
              <span className="text-glass-500">. Records inserted after this page opened are excluded. Run the search again to include them.</span>
            </p>
          )}
          {oldestReceiptAt != null && (
            <p>
              <span className="text-glass-500">Oldest insertion receipt in scope: </span>
              <span className="tabular-nums text-glass-300">{formatLocalDateTime(new Date(oldestReceiptAt))}</span>
              <span className="text-glass-500">. A server that has not reported recently may still hold evidence locally.</span>
            </p>
          )}
          {scope && scope.length > 0 && (
            <p>
              <span className="text-glass-500">Servers covered: </span>
              <span className="font-mono text-glass-300 break-all">{scope.join(', ')}</span>
            </p>
          )}
          {serious && (
            <p className="text-yellow-200/90">
              A capture gap means an action happened but was not recorded, so nothing can be concluded from its absence.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
